import {
  type ExtendedAppointmentAttachment,
  type Operation,
  type Opportunity,
  deleteFile,
  downloadFile,
  shareAttachmentToHomeProject,
  uploadFile,
  useHomeProjectsAttachments,
} from "@repo/api";
import { t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { getErrorMessage, getFileUploadErrorInfo } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";

import {
  groupAppointments,
  useGrouppedAppointments,
} from "../appointments/useGrouppedAppointments";
import { FileLoadingEnum } from "./utils";

export const useFiles = (opportunity: Opportunity) => {
  const { feature_flags } = useSettings();
  const { updateToast } = useToast();
  const { opportunityId } = useParams<{ opportunityId: string }>();

  const [loading, setLoading] = useState<FileLoadingEnum | undefined>(
    undefined
  );
  const [loadingFileId, setLoadingFileId] = useState<number | undefined>(
    undefined
  );
  const [fileToBeDeleted, setFileToBeDeleted] = useState<number | undefined>();
  const [confirmDownloadAll, setConfirmDownloadAll] = useState(false);

  const enableB2BHomeProjectAttachment =
    feature_flags?.enable_b2b_home_project_attachment &&
    opportunity.customerType === "Business";

  const { all: appointments } = useGrouppedAppointments(Number(opportunityId));
  const { data: homeProjectAttachments } = useHomeProjectsAttachments(
    enableB2BHomeProjectAttachment
      ? opportunity.customerProjects?.map((hp) => hp.id)
      : undefined,
    opportunity.partyId
  );

  const attachments = opportunity.attachments ?? [];

  const appointmentAttachments = (
    groupAppointments(appointments).all ?? appointments
  ).flatMap(
    (app) =>
      app.additionalInformation?.flatMap(
        (info) =>
          info.attachments?.map(
            (file) =>
              ({
                ...file,
                orderNumber: app.orderNumber,
                appointmentNumber: app.number,
                fileStatus:
                  file.fileStatus ??
                  (isEmpty(file.fileUrl) ? "LEGACY_UNAVAILABLE" : "APPROVED"),
              }) as ExtendedAppointmentAttachment
          ) || []
      ) || []
  );

  const totalFiles = attachments.length + appointmentAttachments.length;

  const mutateAll = () => {
    mutate(`/opportunities/${opportunityId}`);
    mutate(`/opportunities/${opportunityId}/history`);
  };

  const resetLoading = () => {
    setLoading(undefined);
    setLoadingFileId(undefined);
  };

  const onDelete = async (attachmentId: number) => {
    setLoading(FileLoadingEnum.DELETING);
    setLoadingFileId(attachmentId);
    try {
      await deleteFile(opportunityId!, attachmentId);
      mutateAll();
      updateToast({ title: t("prompt.file_deleted") });
    } catch (err) {
      updateToast({
        title: t("prompt.file_deleted_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      resetLoading();
      setFileToBeDeleted(undefined);
    }
  };

  const onDownload = async (
    name: string,
    attachmentId: number,
    operation: Operation,
    type?: string,
    url?: string
  ) => {
    setLoading(
      operation === "DOWNLOAD"
        ? FileLoadingEnum.DOWNLOADING
        : FileLoadingEnum.OPENING
    );
    setLoadingFileId(attachmentId);

    try {
      await downloadFile(
        opportunityId!,
        name,
        attachmentId,
        operation,
        type,
        url
      );
      if (operation === "DOWNLOAD") {
        updateToast({ title: t("prompt.file_downloaded") });
      }
    } catch (err) {
      updateToast({
        title: t("prompt.file_downloaded_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      resetLoading();
    }
  };

  const onUpload = async (files: FileList) => {
    try {
      await Promise.all(
        Array.from(files).map((file) => uploadFile(opportunityId!, file))
      );
      mutateAll();
      updateToast({ title: t("prompt.file_uploaded") });
    } catch (err) {
      const errorInfo = getFileUploadErrorInfo(err);
      updateToast({
        title: errorInfo.title,
        message: errorInfo.message,
        type: "error",
      });
    }
  };

  const handleDownloadAll = async () => {
    setLoading(FileLoadingEnum.DOWNLOADING_ALL);
    setLoadingFileId(undefined);
    const downloads = [
      ...attachments.map((att) =>
        downloadFile(
          opportunityId!,
          att.name,
          att.attachmentId,
          "DOWNLOAD",
          att.type
        )
      ),
      ...appointmentAttachments
        .filter((att) => att.fileStatus === "APPROVED")
        .map((att) =>
          downloadFile(
            opportunityId!,
            att.fileName,
            att.attachmentId,
            "DOWNLOAD",
            "application/octet-stream",
            att.fileUrl
          )
        ),
    ];
    await Promise.allSettled(downloads);
    resetLoading();
    setConfirmDownloadAll(false);
  };

  const handleSharingToHomeProject = async (
    attachmentId: number,
    shared: boolean
  ) => {
    try {
      await shareAttachmentToHomeProject(attachmentId, shared);
      mutateAll();
      updateToast({
        title: shared
          ? t("prompt.attachment_shared")
          : t("prompt.attachment_unshared"),
      });
    } catch (err) {
      updateToast({
        title: shared
          ? t("prompt.attachment_share_failed")
          : t("prompt.attachment_unshare_failed"),
        message: getErrorMessage(err),
        type: "error",
      });
    }
  };

  const allowSharingToHomeProject = !isEmpty(opportunity.customerProjects);

  return {
    loading,
    loadingFileId,
    fileToBeDeleted,
    setFileToBeDeleted,
    attachments,
    appointmentAttachments,
    homeProjectAttachments,
    totalFiles,
    onDelete,
    onDownload,
    onUpload,
    confirmDownloadAll,
    setConfirmDownloadAll,
    handleDownloadAll,
    handleSharingToHomeProject,
    allowSharingToHomeProject,
  };
};
